import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ReportDTOSchema } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const AuditReportRowSchema = z.object({
  report: ReportDTOSchema,
  consultation: z.object({
    id: z.string(),
    patientName: z.string(),
    doctorId: z.string(),
    language: z.string(),
    startedAt: z.string(),
  }),
  doctor: z.object({ id: z.string(), name: z.string() }),
});

export type AuditReportRow = z.infer<typeof AuditReportRowSchema>;

const AuditListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(AuditReportRowSchema),
});

export type AuditStatusFilter = "pending_audit" | "approved" | "revision_requested" | "all";

export function useAuditReports(status: AuditStatusFilter = "pending_audit") {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["audit-reports", status],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/audit/reports?status=${status}&pageSize=100`,
      });
      return AuditListResponseSchema.parse(json).data;
    },
  });
}

export function useAuditReportDetail(id: string | undefined) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["audit-report-detail", id],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/audit/reports/${id}`,
      }) as { success: boolean; data: unknown };
      return json.data as {
        report: z.infer<typeof ReportDTOSchema>;
        consultation: {
          id: string;
          patientName: string;
          doctorId: string;
          language: string;
          startedAt: string;
          endedAt: string | null;
        };
        transcriptions: Array<{ id: string; text: string; isEdited: boolean; createdAt: string }>;
        audioFile: { id: string; storagePath: string; mimeType: string } | null;
        doctor: { id: string; name: string; email: string };
        patient: { id: string; name: string; personalNumber: string } | null;
      };
    },
    enabled: !!id,
  });
}

export function useApproveReport() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "POST", url: `/api/v1/audit/reports/${id}/approve` }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-reports"] });
      queryClient.invalidateQueries({ queryKey: ["audit-report-detail"] });
      toast.success(t("audit.approvedSuccess"));
    },
  });
}

export function useRequestRevision() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, auditorNotes }: { id: string; auditorNotes: string }) =>
      backend({
        method: "POST",
        url: `/api/v1/audit/reports/${id}/revision`,
        data: { auditorNotes },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-reports"] });
      queryClient.invalidateQueries({ queryKey: ["audit-report-detail"] });
      toast.success(t("audit.revisionSent"));
    },
  });
}

export function useSubmitForAudit(consultationId: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: () =>
      backend({
        method: "PATCH",
        url: `/api/v1/consultations/${consultationId}/report/submit`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report", consultationId] });
      toast.success(t("audit.submitSuccess"));
    },
  });
}

export function useResubmitForAudit(consultationId: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: () =>
      backend({
        method: "PATCH",
        url: `/api/v1/consultations/${consultationId}/report/resubmit`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report", consultationId] });
      toast.success(t("audit.resubmitSuccess"));
    },
  });
}
